// NotificationBell.jsx — real in-app notifications (PLAN_web_notifications.md).
//
// v1 is short-interval polling, not WebSockets/SSE — see the plan's §1
// for why (no real-time transport exists in this codebase, nothing here
// needs sub-second delivery). Polls GET {admin|supplier}-api/
// notifications/unread-count every ~35s while mounted; the actual list
// is only fetched when the dropdown is opened, not on every poll tick.
//
// Branches on admin vs supplier session exactly like AdminHeader.jsx's
// own profile-chip logic (getAdminAuthData()/getSupplierAuthData()),
// since this is rendered INSIDE that header for both identity spaces —
// same axios instance choice (adminApi/supplierApi) as every other
// call site that already does this.
import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  IconButton,
  Badge,
  Menu,
  Box,
  Typography,
  Divider,
  MenuItem,
  Button,
  CircularProgress,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { useNavigate } from "react-router-dom";
import { adminApi, supplierApi } from "../../api";
import { getAdminAuthData } from "../../Utils/adminAuth";
import { getSupplierAuthData } from "../../Utils/supplierAuth";
import { formatDateTime } from "../../Utils/formatDate";

const POLL_INTERVAL_MS = 35000;
const LIST_LIMIT = 20;

const NotificationBell = () => {
  const navigate = useNavigate();
  const { user } = getAdminAuthData();
  const { supplier } = getSupplierAuthData();
  const isSupplier = !user && Boolean(supplier);
  const isLoggedIn = Boolean(user || supplier);
  const api = isSupplier ? supplierApi : adminApi;
  const basePath = isSupplier ? "/supplier-api/notifications" : "/admin-api/notifications";

  const [unreadCount, setUnreadCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const pollRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    if (!isLoggedIn) return;
    try {
      const res = await api.get(`${basePath}/unread-count`);
      setUnreadCount(res.data.count || 0);
    } catch (err) {
      // A 401/expired session is already handled by the axios response
      // interceptor (redirects to /login) — nothing extra needed here.
      // Anything else is a transient poll failure, not worth surfacing.
    }
  }, [api, basePath, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) return undefined;
    fetchUnreadCount();
    pollRef.current = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, basePath]);

  const fetchList = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await api.get(`${basePath}?limit=${LIST_LIMIT}`);
      setNotifications(res.data.notifications || []);
    } catch (err) {
      setNotifications([]);
    } finally {
      setListLoading(false);
    }
  }, [api, basePath]);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    fetchList();
  };
  const handleClose = () => setAnchorEl(null);

  const handleRowClick = async (n) => {
    handleClose();
    if (!n.is_read) {
      setUnreadCount((c) => Math.max(0, c - 1));
      setNotifications((prev) => prev.map((row) => (row.notification_id === n.notification_id ? { ...row, is_read: 1 } : row)));
      try {
        await api.post(`${basePath}/${n.notification_id}/read`);
      } catch (err) {
        // Best-effort — the read state already reflects the click locally;
        // a failed mark-read isn't worth blocking navigation over.
      }
    }
    if (n.link_path) navigate(n.link_path);
  };

  const handleMarkAllRead = async (e) => {
    e.stopPropagation();
    setUnreadCount(0);
    setNotifications((prev) => prev.map((row) => ({ ...row, is_read: 1 })));
    try {
      await api.post(`${basePath}/read-all`);
    } catch (err) {
      // Best-effort, same as above.
    }
  };

  if (!isLoggedIn) return null;

  const badgeContent = unreadCount > 9 ? "9+" : unreadCount;

  return (
    <>
      <IconButton onClick={handleOpen} sx={{ color: "#fff" }} aria-label="Notifications">
        <Badge badgeContent={badgeContent} color="error" invisible={unreadCount === 0}>
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { width: 360, maxWidth: "90vw", maxHeight: 480, borderRadius: 3, mt: 1 } }}
      >
        <Box sx={{ px: 2, py: 1.25, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="subtitle2" fontWeight={700}>Notifications</Typography>
          <Button size="small" onClick={handleMarkAllRead} disabled={!unreadCount}>
            Mark all read
          </Button>
        </Box>
        <Divider />
        {listLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              No notifications yet.
            </Typography>
          </Box>
        ) : (
          notifications.map((n) => (
            <MenuItem
              key={n.notification_id}
              onClick={() => handleRowClick(n)}
              sx={{
                whiteSpace: "normal",
                alignItems: "flex-start",
                py: 1.1,
                bgcolor: n.is_read ? "transparent" : "action.hover",
              }}
            >
              <Box sx={{ width: "100%" }}>
                <Typography variant="body2" fontWeight={n.is_read ? 500 : 700}>
                  {n.title}
                </Typography>
                {n.body && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", whiteSpace: "pre-line" }}>
                    {n.body}
                  </Typography>
                )}
                <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 0.25 }}>
                  {formatDateTime(n.created_at)}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
};

export default NotificationBell;
